// src/pages/Pricing.js
"use client";
import React, { useEffect, useState, useMemo } from "react";
import {
  Container, Grid, Paper, Stack, Typography, Button, List, ListItem, ListItemIcon, ListItemText,
  AppBar, Toolbar, Box, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Link as MUILink, IconButton, Tooltip
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "../auth/AuthContext";
import { getPlans, /* startCheckout, openPortal, */ getMe } from "../api/billing";
import PlanBadge from "../components/PlanBadge";
import ThemeToggleButton from "../components/ThemeToggleButton";
import { Link as RouterLink } from "react-router-dom";

const LOGO_LIGHT = "/brand/SemiQolon-black.png";
const LOGO_DARK  = "/brand/SemiQolon.png";

/* --------------------- Helpers --------------------- */
const nfCurrency = (currency = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 0 });

const plural = (n, one, many = undefined) => `${n} ${n === 1 ? one : (many || `${one}s`)}`;
const fmtUnlimited = (n, labelSingular) => (n == null ? `Unlimited ${labelSingular}${labelSingular.endsWith("s") ? "" : "s"}` : plural(n, labelSingular));

function buildFeatures(lim) {
  if (!lim) return ["Details coming soon"];

  const f = [];

  // Assistants
  f.push(lim.agents == null ? "Unlimited assistants" : plural(lim.agents, "assistant"));

  // Per-assistant quotas
  if ("urls_per_agent" in lim) {
    f.push(lim.urls_per_agent == null
      ? "Unlimited URLs per assistant"
      : `${plural(lim.urls_per_agent, "URL")} per assistant`
    );
  }
  if ("files_per_agent" in lim) {
    f.push(lim.files_per_agent == null
      ? "Unlimited files per assistant"
      : `${plural(lim.files_per_agent, "file")} per assistant`
    );
  }

  // File types + images
  const exts = lim.allowed_exts; // null => all
  let typesText;
  if (exts == null) {
    typesText = "All file types";
  } else if (Array.isArray(exts) && exts.length === 1) {
    typesText = `${String(exts[0] || "").toUpperCase()} only`;
  } else if (Array.isArray(exts) && exts.length > 1) {
    typesText = `Allowed: ${exts.map(e => String(e).toUpperCase()).join(", ")}`;
  } else {
    typesText = "No file types allowed";
  }
  if (lim.allow_images === false) {
    f.push(`${typesText} (images not allowed)`);
  } else {
    f.push(`${typesText} + images`);
  }

  // YouTube ingestion per assistant
  if ("allowed_youtube_urls_per_agent" in lim) {
    f.push(
      lim.allowed_youtube_urls_per_agent == null
        ? "Unlimited YouTube links per assistant"
        : `${plural(lim.allowed_youtube_urls_per_agent, "YouTube link")} per assistant`
    );
  }

  // Chat limits
  const conv = lim.max_conversations_per_month;
  const msgs = lim.max_messages_per_conversation;
  if (conv == null && msgs == null) {
    f.push("Unlimited chat");
  } else {
    const convText = conv == null ? "Unlimited" : conv;
    const msgsText = msgs == null ? "Unlimited" : msgs;
    f.push(`Chat: ${convText} conversations / month · ${msgsText} messages / conversation`);
  }

  return f;
}

/* --------------------- Plan Card --------------------- */
function PlanCard({ plan, currentPlan, onSelect, loading, isLoggedIn }) {
  const highlight = plan.code === "PLATINUM";
  const isCurrent = currentPlan ? plan.code === String(currentPlan).toUpperCase() : false;

  const priceBlock = useMemo(() => {
    if (!("price" in plan)) return null;
    const fmt = nfCurrency(plan.currency || "USD");
    return (
      <Typography variant="h4" sx={{ fontWeight: 900 }}>
        {plan.price === 0 ? "Free" : fmt.format(plan.price)}{" "}
        <Typography component="span" color="text.secondary" variant="subtitle2">/mo</Typography>
      </Typography>
    );
  }, [plan]);

  const features = useMemo(() => buildFeatures(plan.limits), [plan.limits]);

  return (
    <Paper elevation={highlight ? 6 : 2} sx={{ p: 3, borderRadius: 3, height: "100%", position: "relative" }}>
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" sx={{ fontWeight: 900 }}>{plan.code}</Typography>
          <PlanBadge plan={plan.code} />
        </Stack>

        {priceBlock}

        <List dense>
          {features.map((txt, idx) => (
            <ListItem key={`${plan.code}-f-${idx}`} sx={{ py: 0.4 }}>
              <ListItemIcon><CheckCircleIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary={txt} />
            </ListItem>
          ))}
        </List>

        {/* Action area */}
        {isLoggedIn ? (
          <Stack direction="row" spacing={1}>
            <Button
              fullWidth
              variant={highlight ? "contained" : "outlined"}
              disabled={loading || isCurrent}
              onClick={() => onSelect(plan.code)}
            >
              {isCurrent ? "Current plan" : "Choose " + plan.code}
            </Button>
          </Stack>
        ) : (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Log in to upgrade your account.
            </Typography>
            <Button
              component={RouterLink}
              to={`/login?next=${encodeURIComponent("/pricing")}`}
              variant="outlined"
              size="small"
            >
              Login
            </Button>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

/* --------------------- Page --------------------- */
export default function Pricing() {
  const { user } = useAuth();
  const theme = useTheme();
  const [plans, setPlans] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(false);

  // Contact modal state
  const [contactOpen, setContactOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [reason, setReason] = useState("upgrade"); // "upgrade" | "billing"
  const [email, setEmail] = useState("");
  const [copied, setCopied] = useState(false);

  const logoSrc = theme.palette.mode === "dark" ? LOGO_DARK : LOGO_LIGHT;
  const userId = user?.sub;

  useEffect(() => {
    (async () => {
      try {
        const p = await getPlans();        // expects backend JSON with {code, price, currency, limits:{...}}
        setPlans(p);
      } catch (e) {
        console.error("Failed to load plans", e);
      }
      if (userId) {
        try {
          const m = await getMe(userId);
          setMe(m);
        } catch (e) {
          console.warn("getMe failed", e);
        }
      }
    })();
  }, [userId]);

  // best-effort prefill for email
  useEffect(() => {
    const guess =
      (user && (user.email || user.username)) ||
      (me && (me.email || me.username)) ||
      "";
    setEmail(guess || "");
  }, [user, me]);

  const openContact = (why, code = null) => {
    setReason(why);
    setSelectedPlan(code);
    setContactOpen(true);
  };

  // Only reachable when logged in (buttons hidden otherwise)
  const pick = async (code) => {
    openContact("upgrade", code);
  };

  const manage = async () => {
    // Only show/manage when logged in (banner renders only when me exists)
    openContact("billing", null);
  };

  const mailTo = useMemo(() => {
    const subject =
      reason === "billing"
        ? "Billing help request"
        : `Plan upgrade request: ${selectedPlan || ""}`;
    const lines = [
      `Hello Semiqolon Studio,`,
      ``,
      reason === "billing"
        ? `I'd like assistance with my billing/plan.`
        : `I'd like to upgrade my plan to: ${selectedPlan}.`,
      `Registered email: ${email || "<please fill>"} `,
      ``,
      `Thanks!`,
    ];
    const body = encodeURIComponent(lines.join("\n"));
    return `mailto:info@semiqolonstudio.com?subject=${encodeURIComponent(subject)}&body=${body}`;
  }, [reason, selectedPlan, email]);

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText("info@semiqolonstudio.com");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {}
  };

  return (
    <>
      <AppBar position="sticky" color="inherit" elevation={0} sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
        <Toolbar sx={{ gap: 2 }}>
          <Box component={RouterLink} to="/" sx={{ display: "flex", alignItems: "center", gap: 1.25, textDecoration: "none" }}>
            <Box component="img" src={logoSrc} alt="SemiQolon Studio" sx={{ height: 32 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800, flexGrow: 1 }}></Typography>
          {me?.plan && <PlanBadge plan={me.plan} />}
          <ThemeToggleButton />
          <Button component={RouterLink} to={user ? "/studio" : "/login"} variant="contained">
            {user ? "Open Studio" : "Login"}
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        {me && (
          <Stack direction="row" alignItems="center" justifyContent="center" sx={{ mb: 3 }} spacing={1.5}>
            <Typography variant="body1" color="text.secondary">
              You’re on
            </Typography>
            <PlanBadge plan={me.plan} />
            {me.plan !== "FREE" && (
              <Button onClick={manage} size="small" variant="outlined" disabled={loading}>
                {loading ? <CircularProgress size={16} /> : "Manage billing"}
              </Button>
            )}
          </Stack>
        )}

        <Grid container spacing={2.5}>
          {plans.map(p => (
            <Grid item xs={12} md={4} key={p.code}>
              <PlanCard
                plan={p}
                currentPlan={me?.plan}
                onSelect={pick}
                loading={loading}
                isLoggedIn={Boolean(user)}
              />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Contact / Placeholder Billing Modal */}
      <Dialog open={contactOpen} onClose={() => setContactOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>
          {reason === "billing" ? "Need help with billing?" : "Thanks for choosing a plan!"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body1">
              {reason === "billing"
                ? "We’re setting up the billing portal. Meanwhile, drop us a line and we’ll assist you right away."
                : (
                  <>
                    We’re thrilled you’d like to{" "}
                    <strong>upgrade{selectedPlan ? ` to ${selectedPlan}` : ""}</strong>.
                    Our payment gateway is launching soon. Until then, email us and we’ll update your plan manually.
                  </>
                )}
            </Typography>

            {selectedPlan && (
              <Typography variant="body2" color="text.secondary">
                Selected plan: <strong>{selectedPlan}</strong>
              </Typography>
            )}

            <TextField
              label="Your registered email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              autoFocus
            />

            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2">Write to:&nbsp;</Typography>
              <MUILink href="mailto:info@semiqolonstudio.com">info@semiqolonstudio.com</MUILink>
              <Tooltip title={copied ? "Copied!" : "Copy address"}>
                <IconButton onClick={copyAddress} size="small">
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              Tip: include the email you use to sign in so we can match your account instantly.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setContactOpen(false)}>Close</Button>
          <Button
            variant="contained"
            endIcon={<OpenInNewIcon />}
            component={MUILink}
            href={mailTo}
            onClick={() => setContactOpen(false)}
          >
            Email us now
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
